import { useCallback, useEffect, useRef, useState } from 'react';
import { ProgressiveImageTabletMobileProps } from '@v2/components/imageHandling/components/ProgressiveImage/ProgressiveImageTabletMobile';
import { IMAGE_ZOOM_CONSTRAINTS } from '@v2/components/imageHandling/constants';
import { registerImageViewController, setImageViewTransform } from '@v2/components/imageHandling/components/DrawingCanvas/helpers/imageViewTransformStore';
import type { ImageViewController } from '@v2/components/imageHandling/components/DrawingCanvas/types';
import type { ZoomableRef } from '@likashefqet/react-native-image-zoom';

interface UseManageImageScalingTabletMobileParams extends Pick<ProgressiveImageTabletMobileProps, 'onZoomStart' | 'onImagePress'> {
  /** documentId to key the published transform under — drives the annotation overlay. */
  documentId?: number;
}

export const useManageImageScalingTabletMobile = ({ onZoomStart, onImagePress, documentId }: UseManageImageScalingTabletMobileParams) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [currentScale, setCurrentScale] = useState(IMAGE_ZOOM_CONSTRAINTS.min);
  const zoomableRef = useRef<ZoomableRef>(null);
  const publishViewTransformTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const publishViewTransform = useCallback(() => {
    if (documentId == null) return;
    const info = zoomableRef.current?.getInfo?.();
    if (!info) return;
    const { scale, translateX, translateY } = info.transformations;
    setImageViewTransform(documentId, { zoom: scale, panX: translateX, panY: translateY });
  }, [documentId]);

  const handleZoomStart = useCallback(() => {
    onZoomStart?.();
  }, [onZoomStart]);

  const handleZoomEnd = useCallback(() => {
    // Read the live scale from Zoomable instead of `currentScale` — `currentScale` is only refreshed
    // in `handleResetAnimationEnd`, so using it here produced stale `isZoomed` right after a pinch.
    const liveScale = zoomableRef.current?.getInfo?.()?.transformations.scale ?? currentScale;
    setCurrentScale(liveScale);
    setIsZoomed(liveScale > IMAGE_ZOOM_CONSTRAINTS.min);
    publishViewTransform();
  }, [currentScale, publishViewTransform]);

  const handleProgrammaticZoom = useCallback(
    (zoomType: any) => {
      if (zoomType === 'zoomIn') {
        setIsZoomed(true);
      } else if (zoomType === 'zoomOut') {
        setIsZoomed(false);
      }
      // Zoomable animates scale/pan via Reanimated `withTiming` (~300ms). Publishing once now
      // captures the start values; re-publish after the animation completes so the store (and
      // therefore the annotation overlay) tracks the final transform. Timer id is tracked so a
      // rapid re-trigger or an unmount during the window cancels the pending publish.
      publishViewTransform();
      if (publishViewTransformTimerRef.current) clearTimeout(publishViewTransformTimerRef.current);
      publishViewTransformTimerRef.current = setTimeout(() => {
        publishViewTransformTimerRef.current = null;
        publishViewTransform();
      }, 350);
    },
    [publishViewTransform]
  );

  useEffect(
    () => () => {
      if (publishViewTransformTimerRef.current) {
        clearTimeout(publishViewTransformTimerRef.current);
        publishViewTransformTimerRef.current = null;
      }
    },
    []
  );

  const handleSingleTap = useCallback(() => {
    if (!isZoomed) {
      onImagePress?.();
    }
  }, [isZoomed, onImagePress]);

  const handleDoubleTap = useCallback(
    (zoomType: any) => {
      if (zoomType === 'zoomIn') {
        setIsZoomed(true);
      } else if (zoomType === 'zoomOut') {
        setIsZoomed(false);
      }
      publishViewTransform();
    },
    [publishViewTransform]
  );

  // Register an imperative bridge so the annotation controller (`useImageViewTransformController`)
  // can drive Zoomable on native — we forward `zoom`, `reset`, and a focal-point fallback.
  useEffect(() => {
    if (documentId == null) return;
    const adapter: ImageViewController = {
      zoom: (event) => zoomableRef.current?.zoom?.(event),
      reset: () => zoomableRef.current?.reset?.(),
      getContainerCenter: () => zoomableRef.current?.getInfo?.().container.center ?? null,
    };
    return registerImageViewController(documentId, adapter);
  }, [documentId]);

  const handleResetAnimationEnd = useCallback(
    (finished?: boolean, values?: any) => {
      if (finished && values?.SCALE?.lastValue) {
        setCurrentScale(values.SCALE.lastValue);
        setIsZoomed(values.SCALE.lastValue > IMAGE_ZOOM_CONSTRAINTS.min);
      } else if (finished) {
        setCurrentScale(IMAGE_ZOOM_CONSTRAINTS.min);
        setIsZoomed(false);
      }
      publishViewTransform();
    },
    [publishViewTransform]
  );

  return {
    zoomableRef,
    handleZoomStart,
    handleZoomEnd,
    handleProgrammaticZoom,
    handleSingleTap,
    handleDoubleTap,
    handleResetAnimationEnd,
  };
};
